import { useEffect, useRef, useState } from 'react';
import { precallEventData, runPrecallTest, type PrecallResult } from '@shareview/tele-engine-precall';
import { createLiveKitNetworkProbe } from '@shareview/tele-engine-transport-livekit';
import { createWebMediaAdapter, requestMediaToken } from '@shareview/tele-engine-web';
import { appConfig, profileFor, tabTag, type CallOptions } from './config';
import type { PocTelemetry } from './telemetry';

interface PrecallProps {
  opts: CallOptions;
  /** A current access token for the Calls API (refreshed if needed). */
  accessToken: () => Promise<string>;
  telemetry: PocTelemetry | null;
  onResult: (result: PrecallResult) => void;
}

/**
 * Guest pre-call screen: camera, microphone and network (uplink, RTT, loss, TURN relay) against a
 * separate test room, a recommended quality tier applied to the join, and a precall-result event.
 * `?precall=1` runs it on load.
 */
export function Precall({ opts, accessToken, telemetry, onResult }: PrecallProps) {
  const [phase, setPhase] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<PrecallResult>();
  const [error, setError] = useState<string>();
  const started = useRef(false);

  const run = async () => {
    setPhase('running');
    setError(undefined);
    try {
      const test = await requestMediaToken({
        apiBaseUrl: appConfig.apiBaseUrl,
        roomId: `tele-poc-precall-${tabTag()}`,
        accessToken: await accessToken(),
        displayName: 'precall',
      });
      const r = await runPrecallTest({
        media: createWebMediaAdapter(),
        profile: profileFor(opts),
        network: createLiveKitNetworkProbe({ url: test.serverUrl, token: test.token }),
      });
      telemetry?.collector.recordEvent('precall-result', { data: precallEventData(r) });
      setResult(r);
      onResult(r);
      setPhase('done');
    } catch (e) {
      setError(String(e));
      setPhase('error');
    }
  };

  useEffect(() => {
    if (opts.precall && !started.current) {
      started.current = true;
      void run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.precall]);

  const n = result?.network;
  return (
    <section className="precall" data-testid="precall" data-phase={phase}>
      <h3>Before you join</h3>
      <button data-testid="run-precall" disabled={phase === 'running'} onClick={() => void run()}>
        {phase === 'running' ? 'Checking camera, microphone and network...' : 'Check camera, microphone and network'}
      </button>
      {error && <pre className="error">{error}</pre>}
      {result && (
        <ul>
          <li data-testid="precall-camera">
            Camera:{' '}
            {result.camera?.ok
              ? `ok, ${result.camera.width}x${result.camera.height}`
              : `not available (${result.camera?.error})`}
          </li>
          <li data-testid="precall-mic">
            Microphone:{' '}
            {result.microphone?.ok
              ? `ok, peak level ${result.microphone.peakLevel ?? '?'}`
              : `not available (${result.microphone?.error})`}
          </li>
          <li data-testid="precall-network">
            Network:{' '}
            {n
              ? `${n.mediaOk ? 'connected' : 'no media connection'} (${n.directCandidateType ?? '?'}), uplink ~${n.uplinkKbps ?? '?'} kbps, ` +
                `RTT ${n.roundTripTimeMs ?? '?'} ms, loss ${n.packetLossPercent ?? '?'}%, TURN relay ${n.relayOk ? `ok (${n.relayProtocol})` : 'failed'}`
              : 'not tested'}
          </li>
          <li data-testid="precall-tier">
            Recommended quality: <strong>{result.recommendedTier}</strong>
            {result.reasons.length ? ` (${result.reasons.join('; ')})` : ''}
          </li>
        </ul>
      )}
    </section>
  );
}
